import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ProductService,
  ProductFormData,
  Product,
} from "../services/productService";

type Category = {
  id: string;
  name: string;
};

type ProductType = {
  id: string;
  name: string;
};

const AdminProductForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditMode = Boolean(id);

  const API_BASE =
    process.env.REACT_APP_BASE_URL || "http://localhost:5000/api";

  const [authChecked, setAuthChecked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  /* ================= FORM STATE ================= */
  const [form, setForm] = useState<ProductFormData>({
    name: "",
    price: "",
    description: "",
    stock: "",
    categoryId: "",
    productTypeId: "",
    images: null,
  });

  const [categories, setCategories] = useState<Category[]>([]);
  const [productTypes, setProductTypes] = useState<ProductType[]>([]);
  const [imagePreviews, setImagePreviews] = useState<string[]>([]);

  /* ================= AUTH ================= */
  useEffect(() => {
    const checkSession = async () => {
      try {
        const res = await fetch(`${API_BASE}/session`, {
          credentials: "include",
        });
        const data = await res.json();
        if (!data.user) navigate("/login");
      } catch {
        navigate("/login");
      } finally {
        setAuthChecked(true);
      }
    };
    checkSession();
  }, [API_BASE, navigate]);

  /* ================= LOAD DATA ================= */
  useEffect(() => {
    if (!authChecked) return;

    fetch(`${API_BASE}/categories`, { credentials: "include" })
      .then((r) => r.json())
      .then(setCategories)
      .catch(console.error);

    fetch(`${API_BASE}/product-types`, { credentials: "include" })
      .then((r) => r.json())
      .then(setProductTypes)
      .catch(console.error);

    if (isEditMode && id) {
      ProductService.getProductById(id)
        .then((p: Product) => {
          setForm({
            name: p.name,
            price: p.price.toString(),
            description: p.description || "",
            stock: p.stock_quantity.toString(),
            categoryId: p.category?.id?.toString() || "",
            productTypeId: (p as any).product_type?.id?.toString() || (p.productTypeId as any) || "",
            images: null,
          });

          setImagePreviews(
            p.images?.map((img) => img.signedUrl) || []
          );
        })
        .catch(console.error);
    }
  }, [API_BASE, authChecked, id, isEditMode]);

  /* ================= HANDLERS ================= */
  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    setForm((prev) => ({ ...prev, images: files }));
    setImagePreviews(
      files ? Array.from(files).map((f) => URL.createObjectURL(f)) : []
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage("");

    try {
      if (isEditMode && id) {
        await ProductService.updateProductWithImages(id, form);
        setMessage("✅ Product updated successfully");
      } else {
        await ProductService.createProductWithImages(form);
        setMessage("✅ Product created successfully");
        setForm({
          name: "",
          price: "",
          description: "",
          stock: "",
          categoryId: "",
          productTypeId: "",
          images: null,
        });
        setImagePreviews([]);
      }
    } catch (err: any) {
      console.error(err);
      setMessage(err?.response?.data?.message || "❌ Failed to save product");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;
    if (!window.confirm("Delete this product?")) return;

    try {
      await ProductService.deleteProduct(id);
      navigate("/admin/products");
    } catch {
      alert("Failed to delete product");
    }
  };

  if (!authChecked) {
    return (
      <div className="flex h-screen items-center justify-center text-gray-500">
        Loading…
      </div>
    );
  }

  /* ================= UI ================= */
  return (
    <div className="min-h-screen bg-gray-100 p-8">
      <div className="mx-auto max-w-5xl">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold">
              {isEditMode ? "Edit Product" : "Create Product"}
            </h1>
            <p className="text-sm text-gray-500">
              {isEditMode
                ? "Update product details"
                : "Add a new product to your catalog"}
            </p>
          </div>

          {isEditMode && (
            <button
              onClick={handleDelete}
              className="rounded-md bg-red-600 px-4 py-2 text-white hover:bg-red-700"
            >
              Delete
            </button>
          )}
        </div>

        <form
          onSubmit={handleSubmit}
          className="rounded-xl border border-gray-300 bg-white shadow"
        >
          <table className="w-full border-collapse text-sm">
            <tbody>
              {[
                ["Product Name", "name", "text"],
                ["Price (UGX)", "price", "number"],
                ["Stock", "stock", "number"],
              ].map(([label, name, type]) => (
                <tr key={name} className="border-b">
                  <td className="w-1/3 px-6 py-4 font-medium">{label}</td>
                  <td className="px-6 py-4">
                    <input
                      type={type}
                      name={name}
                      value={(form as any)[name]}
                      onChange={handleChange}
                      required
                      className="w-full rounded-md border px-3 py-2"
                    />
                  </td>
                </tr>
              ))}

              <tr className="border-b">
                <td className="px-6 py-4 font-medium">Category</td>
                <td className="px-6 py-4">
                  <select
                    name="categoryId"
                    value={form.categoryId}
                    onChange={handleChange}
                    required
                    className="w-full rounded-md border px-3 py-2"
                  >
                    <option value="">Select category</option>
                    {categories.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </td>
              </tr>

              <tr className="border-b">
                <td className="px-6 py-4 font-medium">Product Type</td>
                <td className="px-6 py-4">
                  <select
                    name="productTypeId"
                    value={(form as any).productTypeId}
                    onChange={handleChange}
                    required
                    className="w-full rounded-md border px-3 py-2"
                  >
                    <option value="">Select type</option>
                    {productTypes.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.name}
                      </option>
                    ))}
                  </select>
                </td>
              </tr>

              <tr className="border-b">
                <td className="px-6 py-4 font-medium">Description</td>
                <td className="px-6 py-4">
                  <textarea
                    name="description"
                    value={form.description}
                    onChange={handleChange}
                    rows={4}
                    className="w-full rounded-md border px-3 py-2"
                  />
                </td>
              </tr>

              <tr>
                <td className="px-6 py-4 font-medium">Images</td>
                <td className="px-6 py-4">
                  <input type="file" multiple onChange={handleFileChange} />
                  <div className="mt-4 grid grid-cols-4 gap-3">
                    {imagePreviews.map((src, i) => (
                      <img
                        key={i}
                        src={src}
                        className="h-24 rounded border object-cover"
                      />
                    ))}
                  </div>
                </td>
              </tr>
            </tbody>
          </table>

          <div className="flex items-center justify-between border-t bg-gray-50 px-6 py-4">
            <p className="text-sm text-gray-600">{message}</p>
            <button
              disabled={loading}
              className="rounded-md bg-indigo-600 px-6 py-2 text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {loading
                ? isEditMode
                  ? "Updating..."
                  : "Creating..."
                : isEditMode
                ? "Update Product"
                : "Create Product"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AdminProductForm;
